import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import PrivateRoute from 'components/Navigation/ProtectedRoute';
import OutlineButton from 'components/OutlineButton';
import { showErrorToast, showSuccessToast } from 'components/Toast';
import SolidButton from 'components/SolidButton';

import { BUTTON_COLORS } from 'utils/enums';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';

export default function ChangePage() {
  const history = useNavigate();
  const auth = useAuth();

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [loading, setLoading] = useState(false);
  const [currentChange, setCurrentChange] = useState<any>({});

  const submitTest = async () => {
    setLoading(true);
    let result: any = await generalApi.post('/test_change', {
      change: currentChange?.change,
      sp_compra: currentChange?.sp_compra,
      sp_venta: currentChange?.sp_venta,
      rangesExchange: currentChange?.ranges?.map((obj: any) => ({
        importe: obj.importe,
        sp_compra: obj.sp_compra,
        sp_venta: obj.sp_venta,
      })),
    });
    setLoading(false);
    if (!result.success) return showErrorToast(result.data.err);
    setCurrentChange(result.data);
    showSuccessToast('Cambio realizado');
  };

  const submitSave = async () => {
    setLoading(true);
    let result: any = await generalApi.post('/set_change', {
      change: currentChange?.change,
      sp_compra: currentChange?.sp_compra,
      sp_venta: currentChange?.sp_venta,
      rangesExchange: currentChange?.ranges?.map((obj: any) => ({
        importe: obj.importe,
        sp_compra: obj.sp_compra,
        sp_venta: obj.sp_venta,
      })),
    });
    setLoading(false);
    if (!result.success) return showErrorToast(result.data.err);
    setCurrentChange(result.data);
    showSuccessToast('Cambio realizado');
  };

  useEffect(() => {
    const getCurrentChange = async () => {
      const result = await generalApi.get('/change_settings');
      if (!result.success) {
        showErrorToast(result.message);
        setLoading(false);
        return;
      }
      setCurrentChange(result.data);
    };
    getCurrentChange();
  }, [generalApi]);

  return (
    <PrivateRoute>
      <div className="text-[#1B205A]">
        <div className="pb-8 2xl:px-24">
          <div className="mt-6 mb-2 grid sm:grid-cols-12">
            <p className="col-span-8 text-2xl font-bold">Tipo de Cambio</p>
            <p className="col-span-2 hidden justify-center text-2xl font-bold sm:flex">BID</p>
            <p className="col-span-2 hidden justify-center text-2xl font-bold sm:flex">OFFER</p>
          </div>
          <div className="shadow-md sm:rounded-lg">
            <div className="grid grid-cols-6 bg-primary-600 py-2 text-center text-white sm:grid-cols-12 sm:rounded-t-lg">
              <span className="col-span-2 sm:col-span-4">Cambio</span>
              <span className="col-span-2">Spread Compra</span>
              <span className="col-span-2">Spread Venta</span>
            </div>
            <div className="grid grid-cols-6 bg-white text-center sm:grid-cols-12 sm:rounded-b-lg">
              <span className="col-span-2 py-4 px-1 sm:col-span-4">
                <input
                  className="w-full border-0 text-center sm:w-auto"
                  type="text"
                  value={currentChange?.change}
                  onChange={(e) => {
                    setCurrentChange({ ...currentChange, change: e.target.value });
                  }}
                />
              </span>
              <span className="col-span-2 py-4 px-1">
                <input
                  className="w-full border-0 text-center sm:w-auto"
                  type="text"
                  value={currentChange?.sp_compra}
                  onChange={(e) => {
                    setCurrentChange({ ...currentChange, sp_compra: e.target.value });
                  }}
                />
              </span>
              <span className="col-span-2 py-4 px-1">
                <input
                  className="w-full border-0 text-center sm:w-auto"
                  type="text"
                  value={currentChange?.sp_venta}
                  onChange={(e) => {
                    setCurrentChange({ ...currentChange, sp_venta: e.target.value });
                  }}
                />
              </span>
              <span className="col-span-3 flex items-center justify-center py-4 font-bold sm:col-span-2">
                <span className="sm:hidden">BID: {currentChange?.BID}</span>
                <span className="hidden sm:block">{currentChange?.BID}</span>
              </span>
              <span className="col-span-3 flex items-center justify-center py-4 font-bold sm:col-span-2">
                <span className="sm:hidden">OFFER: {currentChange?.OFFER}</span>
                <span className="hidden sm:block">{currentChange?.OFFER}</span>
              </span>
            </div>
          </div>

          <div className="mt-8 mb-2 grid sm:grid-cols-12">
            <p className="col-span-4 text-2xl font-bold">Rangos de Precios Izidólar</p>
            <p className="col-span-4 hidden justify-center text-2xl font-bold sm:flex">
              Spread Mejora
            </p>
          </div>
          <div className="shadow-md sm:rounded-lg">
            <div className="grid grid-cols-6 bg-primary-600 py-2 text-center text-white sm:grid-cols-12 sm:rounded-t-lg">
              <span className="col-span-2 sm:col-span-4">Importe $</span>
              <span className="col-span-2">Spread Compra</span>
              <span className="col-span-2">Spread Venta</span>
            </div>
            <div className="divide-y-2 divide-gray-100 bg-white text-center sm:rounded-b-lg">
              <div className="grid grid-cols-6 sm:grid-cols-12">
                <span className="col-span-2 py-4 px-1 sm:col-span-4">
                  <input
                    className="w-full border-0 text-center sm:w-auto"
                    type="text"
                    value={currentChange?.ranges?.[0]?.importe
                      .toString()
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    onChange={(e) => {
                      setCurrentChange({
                        ...currentChange,
                        ranges: [
                          {
                            ...currentChange?.ranges[0],
                            importe: e.target.value.replace(/\D/g, ''),
                          },
                          currentChange?.ranges[1],
                          currentChange?.ranges[2],
                          currentChange?.ranges[3],
                        ],
                      });
                    }}
                  />
                </span>
                <span className="col-span-2 py-4 px-1">
                  <input
                    className="w-full border-0 text-center sm:w-auto"
                    type="text"
                    value={currentChange?.ranges?.[0]?.sp_compra}
                    onChange={(e) => {
                      setCurrentChange({
                        ...currentChange,
                        ranges: [
                          {
                            ...currentChange?.ranges[0],
                            sp_compra: e.target.value,
                          },
                          currentChange?.ranges[1],
                          currentChange?.ranges[2],
                          currentChange?.ranges[3],
                        ],
                      });
                    }}
                  />
                </span>
                <span className="col-span-2 py-4 px-1">
                  <input
                    className="w-full border-0 text-center sm:w-auto"
                    type="text"
                    value={currentChange?.ranges?.[0]?.sp_venta}
                    onChange={(e) => {
                      setCurrentChange({
                        ...currentChange,
                        ranges: [
                          {
                            ...currentChange?.ranges[0],
                            sp_venta: e.target.value,
                          },

                          currentChange?.ranges[1],
                          currentChange?.ranges[2],
                          currentChange?.ranges[3],
                        ],
                      });
                    }}
                  />
                </span>
                <span className="col-span-3 flex items-center justify-center pb-4 font-bold sm:col-span-2 sm:pt-4">
                  <span className="sm:hidden">BID: {currentChange?.ranges?.[0]?.BID}</span>
                  <span className="hidden sm:block">{currentChange?.ranges?.[0]?.BID}</span>
                </span>
                <span className="col-span-3 flex items-center justify-center pb-4 font-bold sm:col-span-2 sm:pt-4">
                  <span className="sm:hidden">OFFER: {currentChange?.ranges?.[0]?.OFFER}</span>
                  <span className="hidden sm:block">{currentChange?.ranges?.[0]?.OFFER}</span>
                </span>
              </div>
              <div className="grid grid-cols-6 sm:grid-cols-12">
                <span className="col-span-2 py-2 px-1 sm:col-span-4">
                  <input
                    className="w-full border-0 text-center sm:w-auto"
                    type="text"
                    value={currentChange?.ranges?.[1]?.importe
                      .toString()
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    onChange={(e) => {
                      setCurrentChange({
                        ...currentChange,
                        ranges: [
                          currentChange?.ranges[0],
                          {
                            ...currentChange?.ranges[1],
                            importe: e.target.value.replace(/\D/g, ''),
                          },
                          currentChange?.ranges[2],
                          currentChange?.ranges[3],
                        ],
                      });
                    }}
                  />
                </span>
                <span className="col-span-2 py-2 px-1">
                  <input
                    className="w-full border-0 text-center sm:w-auto"
                    type="text"
                    value={currentChange?.ranges?.[1]?.sp_compra}
                    onChange={(e) => {
                      setCurrentChange({
                        ...currentChange,
                        ranges: [
                          currentChange?.ranges[0],
                          {
                            ...currentChange?.ranges[1],
                            sp_compra: e.target.value,
                          },
                          currentChange?.ranges[2],
                          currentChange?.ranges[3],
                        ],
                      });
                    }}
                  />
                </span>
                <span className="col-span-2 py-2 px-1">
                  <input
                    className="w-full border-0 text-center sm:w-auto"
                    type="text"
                    value={currentChange?.ranges?.[1]?.sp_venta}
                    onChange={(e) => {
                      setCurrentChange({
                        ...currentChange,
                        ranges: [
                          currentChange?.ranges[0],
                          {
                            ...currentChange?.ranges[1],
                            sp_venta: e.target.value,
                          },
                          currentChange?.ranges[2],
                          currentChange?.ranges[3],
                        ],
                      });
                    }}
                  />
                </span>
                <span className="col-span-3 flex items-center justify-center pb-4 font-bold sm:col-span-2 sm:pt-4">
                  <span className="sm:hidden">BID: {currentChange?.ranges?.[1]?.BID}</span>
                  <span className="hidden sm:block">{currentChange?.ranges?.[1]?.BID}</span>
                </span>
                <span className="col-span-3 flex items-center justify-center pb-4 font-bold sm:col-span-2 sm:pt-4">
                  <span className="sm:hidden">OFFER: {currentChange?.ranges?.[1]?.OFFER}</span>
                  <span className="hidden sm:block">{currentChange?.ranges?.[1]?.OFFER}</span>
                </span>
              </div>
              <div className="grid grid-cols-6 sm:grid-cols-12">
                <span className="col-span-2 py-2 px-1 sm:col-span-4">
                  <input
                    className="w-full border-0 text-center sm:w-auto"
                    type="text"
                    value={currentChange?.ranges?.[2]?.importe
                      .toString()
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    onChange={(e) => {
                      setCurrentChange({
                        ...currentChange,
                        ranges: [
                          currentChange?.ranges[0],
                          currentChange?.ranges[1],
                          {
                            ...currentChange?.ranges[2],
                            importe: e.target.value.replace(/\D/g, ''),
                          },
                          currentChange?.ranges[3],
                        ],
                      });
                    }}
                  />
                </span>
                <span className="col-span-2 py-2 px-1">
                  <input
                    className="w-full border-0 text-center sm:w-auto"
                    type="text"
                    value={currentChange?.ranges?.[2]?.sp_compra}
                    onChange={(e) => {
                      setCurrentChange({
                        ...currentChange,
                        ranges: [
                          currentChange?.ranges[0],
                          currentChange?.ranges[1],
                          {
                            ...currentChange?.ranges[2],
                            sp_compra: e.target.value,
                          },
                          currentChange?.ranges[3],
                        ],
                      });
                    }}
                  />
                </span>
                <span className="col-span-2 py-2 px-1">
                  <input
                    className="w-full border-0 text-center sm:w-auto"
                    type="text"
                    value={currentChange?.ranges?.[2]?.sp_venta}
                    onChange={(e) => {
                      setCurrentChange({
                        ...currentChange,
                        ranges: [
                          currentChange?.ranges[0],
                          currentChange?.ranges[1],
                          {
                            ...currentChange?.ranges[2],
                            sp_venta: e.target.value,
                          },
                          currentChange?.ranges[3],
                        ],
                      });
                    }}
                  />
                </span>
                <span className="col-span-3 flex items-center justify-center pb-4 font-bold sm:col-span-2 sm:pt-4">
                  <span className="sm:hidden">BID: {currentChange?.ranges?.[2]?.BID}</span>
                  <span className="hidden sm:block">{currentChange?.ranges?.[2]?.BID}</span>
                </span>
                <span className="col-span-3 flex items-center justify-center pb-4 font-bold sm:col-span-2 sm:pt-4">
                  <span className="sm:hidden">OFFER: {currentChange?.ranges?.[2]?.OFFER}</span>
                  <span className="hidden sm:block">{currentChange?.ranges?.[2]?.OFFER}</span>
                </span>
              </div>
              <div className="grid grid-cols-6 sm:grid-cols-12">
                <span className="col-span-2 py-2 px-1 sm:col-span-4">
                  <input
                    className="w-full border-0 text-center sm:w-auto"
                    type="text"
                    value={currentChange?.ranges?.[3]?.importe
                      .toString()
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    onChange={(e) => {
                      setCurrentChange({
                        ...currentChange,
                        ranges: [
                          currentChange?.ranges[0],
                          currentChange?.ranges[1],
                          currentChange?.ranges[2],
                          {
                            ...currentChange?.ranges[3],
                            importe: e.target.value.replace(/\D/g, ''),
                          },
                        ],
                      });
                    }}
                  />
                </span>
                <span className="col-span-2 py-2 px-1">
                  <input
                    className="w-full border-0 text-center sm:w-auto"
                    type="text"
                    value={currentChange?.ranges?.[3]?.sp_compra}
                    onChange={(e) => {
                      setCurrentChange({
                        ...currentChange,
                        ranges: [
                          currentChange?.ranges[0],
                          currentChange?.ranges[1],
                          currentChange?.ranges[2],
                          {
                            ...currentChange?.ranges[3],
                            sp_compra: e.target.value,
                          },
                        ],
                      });
                    }}
                  />
                </span>
                <span className="col-span-2 py-2 px-1">
                  <input
                    className="w-full border-0 text-center sm:w-auto"
                    type="text"
                    value={currentChange?.ranges?.[3]?.sp_venta}
                    onChange={(e) => {
                      setCurrentChange({
                        ...currentChange,
                        ranges: [
                          currentChange?.ranges[0],
                          currentChange?.ranges[1],
                          currentChange?.ranges[2],
                          {
                            ...currentChange?.ranges[3],
                            sp_venta: e.target.value,
                          },
                        ],
                      });
                    }}
                  />
                </span>
                <span className="col-span-3 flex items-center justify-center pb-4 font-bold sm:col-span-2 sm:pt-4">
                  <span className="sm:hidden">BID: {currentChange?.ranges?.[3]?.BID}</span>
                  <span className="hidden sm:block">{currentChange?.ranges?.[3]?.BID}</span>
                </span>
                <span className="col-span-3 flex items-center justify-center pb-4 font-bold sm:col-span-2 sm:pt-4">
                  <span className="sm:hidden">OFFER: {currentChange?.ranges?.[3]?.OFFER}</span>
                  <span className="hidden sm:block">{currentChange?.ranges?.[3]?.OFFER}</span>
                </span>
              </div>
            </div>
          </div>

          <div className="my-6 flex flex-col items-center justify-center space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
            <OutlineButton
              className="w-auto px-12"
              color={BUTTON_COLORS.BLUE}
              isLoading={loading}
              onClick={() => submitTest()}
            >
              Actualizar
            </OutlineButton>
            <SolidButton
              className="w-auto px-16"
              color={BUTTON_COLORS.BLUE}
              isLoading={loading}
              onClick={() => submitSave()}
            >
              Guardar
            </SolidButton>
          </div>
        </div>
      </div>
    </PrivateRoute>
  );
}
